import React, { useContext, useEffect, useState } from "react";
import { decodeJwt } from "jose";
import { toast } from "react-toastify";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  DefaultLayout,
  PrivateRoute,
  TokenComponent,
  Warning,
} from "@client/shared/components";
import { getUserData, getToken, replaceToken } from "@client/utils";
import {
  noop,
  timezoneMapping,
  ACCESS_DENIED_CODES,
} from "@client/shared/constants";
import { AppContext } from "@client/shared/contexts";
import { AppService } from "@client/shared/services";
import Dashboard from "./dashboard";
import Users from "./users";
import Admin from "./admin";
import AccessDenied from "./access-denied";
import DefaultCallerId from "./default-caller-id";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import {
  UsersBlue,
  UsersWhite,
  VoicemailBlue,
  VoicemailWhite,
} from "@client/shared/components/icons";

let refreshTokenTimeout = null;

const defaultState = {
  warningDialog: [],
};

const App = () => {
  const [state, setState] = useState(defaultState);
  const { setAppData } = useContext(AppContext);
  const userData = (getUserData() || {}).user || {};
  const appAccess = userData?.roleAccess?.appAccess;
  const manageUsers = userData?.roleAccess?.manageUsers;
  const overrideDefaultCallerId = userData?.roleAccess?.overrideDefaultCallerId;
  const handleJwksResponse = (response = {}, callBack = noop) => {
    const { data, error, paging } = response;
    if (
      Array.isArray(error) &&
      ACCESS_DENIED_CODES.includes(error[0].errorCode)
    ) {
      // let message =  (Array.isArray(error) ? error[0].message : error || '').replace("RC Api: ", "") || '';
      // if (!message.includes(".", message.length -1)) {
      //   message += '.';
      // }
      const permissionsName = error?.[0]?.permissionsName;
      let message = permissionsName
        ? `${permissionsName} permission is required to enable this feature. Please contact your RingCentral Account Executive.`
        : (Array.isArray(error) ? error[0].message : error || '').replace("RC Api: ", "") || '';
      setState((prevState) => {
        const isOpened = prevState.warningDialog.some(
          (item) => item === message
        );
        return {
          ...prevState,
          warningDialog: isOpened
            ? prevState.warningDialog
            : [...prevState.warningDialog, message],
        };
      });
      return false;
    }

    if (error) {
      return Array.isArray(error) && error[0].errorCode === "OAU-213"
        ? false
        : toast.error(Array.isArray(error) ? error[0].message : error);
    }
    callBack(data, error, paging);
  };

  const fetchCountryList = async () => {
    const response = await AppService.getCountryList();
    let countryList = [];
    handleJwksResponse(response, (data) => (countryList = data || []));
    setAppData("countryList", countryList);
  };

  const fetchAssignedRoles = async () => {
    const response = await AppService.getAssignedRoles();
    let assignedRoleList = [];
    handleJwksResponse(response, (data) => (assignedRoleList = data || []));
    return assignedRoleList;
  };

  const fetchExtensions = async (withRoles = false) => {
    const individualContactList = [];
    const departmentList = [];
    const assignedRoleList = withRoles ? await fetchAssignedRoles() : [];
    const response = await AppService.getExtensions();
    let extensionList = [];
    handleJwksResponse(response, (data) => (extensionList = data || []));
    extensionList = extensionList
      .filter(
        (extension) => {
          return (
            (extension.status || "").toLowerCase() !== "unassigned" &&
            (extension.status || "").toLowerCase() === "enabled"
          )
        }
      )
      .map((extension) => {
        // getting department
        if (
          extension?.contact?.department &&
          !departmentList.includes(extension?.contact?.department)
        ) {
          departmentList.push(extension?.contact?.department);
        }
        // getting individual contacts
        if (
          (extension.type || "").toLowerCase() === "user" ||
          (extension.type || "").toLowerCase() === "voicemail" ||
          (extension.type || "").toLowerCase() === "virtualuser"

        ) {
          individualContactList.push(extension);
        }
        // adding roles
        if (withRoles) {
          extension.roles =
            (
              assignedRoleList.find(
                (role) => role.extensionId == extension.id
              ) || {}
            ).roles || [];
        }
        return extension;
      });
    setAppData("extensionList", extensionList);
    setAppData(
      "individualContactList",
      individualContactList.sort((contactA, contactB) => {
        const contactAName = (contactA?.name || "").toLowerCase();
        const contactBName = (contactB?.name || "").toLowerCase();
        return contactAName > contactBName
          ? 1
          : contactAName < contactBName
          ? -1
          : 0;
      })
    );
    setAppData(
      "departmentList",
      departmentList.sort((departmentA, departmentB) => {
        const departmentAValue = (departmentA || "").toLowerCase();
        const departmentBValue = (departmentB || "").toLowerCase();
        return departmentAValue > departmentBValue
          ? 1
          : departmentAValue < departmentBValue
          ? -1
          : 0;
      })
    );
  };

  const fetchSites = async () => {
    const response = await AppService.getSites();
    let siteList = [];
    handleJwksResponse(response, (data) => (siteList = data || []));
    const sortedSiteList = siteList.sort((siteA, siteB) => {
      const siteAName = (siteA?.name || "").toLowerCase();
      const siteBName = (siteB?.name || "").toLowerCase();
      return siteAName > siteBName ? 1 : siteAName < siteBName ? -1 : 0;
    });
    setAppData(
      "siteList",
      sortedSiteList.map((site) => site.name)
    );
  };

  const fetchVoicemails = async () => {
    const response = await AppService.getVoicemails();
    let voicemailList = [];
    handleJwksResponse(response, (data) => (voicemailList = data || []));
    setAppData("voicemailList", voicemailList);
  };

  const fetchUserRoles = async () => {
    const response = await AppService.getUserRoles();
    let userRoleList = [];
    handleJwksResponse(response, (data) => (userRoleList = data || []));
    const sortedUserRoleList = userRoleList.sort((roleA, roleB) => {
      const roleAName = (roleA?.displayName || "").toLowerCase();
      const roleBName = (roleB?.displayName || "").toLowerCase();
      return roleAName > roleBName ? 1 : roleAName < roleBName ? -1 : 0;
    });
    setAppData("userRoleList", sortedUserRoleList);
  };

  const fetchPhoneNumbers = async () => {
    const response = await AppService.getPhoneNumbers();
    let phoneNumberList = [];
    handleJwksResponse(response, (data) => (phoneNumberList = data || []));
    setAppData("phoneNumberList", phoneNumberList);
  };

  const fetchAccountInfo = async () => {
    const response = await AppService.getAccountInfo();
    let accountInfo = {};
    handleJwksResponse(response, (data) => (accountInfo = data || {}));
    const timezone = accountInfo?.regionalSettings?.timezone?.name;
    Object.keys(timezoneMapping).map((key) => {
      if (timezone === key) {
        accountInfo.regionalSettings.timezone.name = timezoneMapping[key];
      }
    });
    setAppData("accountInfo", accountInfo);
  };

  const fetchDefaultCallerId = async () => {
    const response = await AppService.getDefaultCallerId();
    let defaultCallerId = {};
    handleJwksResponse(
      response,
      (data) => (defaultCallerId = (data?.account || [])[0] || {})
    );
    setAppData("defaultCallerId", defaultCallerId);
  };

  const refreshToken = async () => {
    const token = getToken();
    const { data } = await AppService.refreshAccessToken(token);
    if (data?.accessToken) {
      replaceToken(data.accessToken);
    }
  };

  const subscribesRefreshToken = (timeout = 50) => {
    clearTimeout(refreshTokenTimeout);
    setTimeout(() => {
      refreshToken();
      subscribesRefreshToken();
    }, 1000 * 60 * timeout);
  };

  const checkTokenStatus = () => {
    const token = getToken();
    if (token) {
      const { exp } = decodeJwt(token);
      const currDate = Date.now() / 1000;
      const remTime = (exp - currDate) / 60;
      subscribesRefreshToken((remTime - 4) > 0 ? (remTime - 4) : 0);
    }
  };

  useEffect(() => {
    checkTokenStatus();
    return () => {
      clearTimeout(refreshTokenTimeout);
    };
  }, []);

  const isAppRoleAdmin = (userData.role?.name || "").toLowerCase() === "admin";
  let drawerList = [
    {
      icon: <VoicemailWhite width="19.5" height="19.5" viewBox="0 -4 18 28" />,
      activeIcon: (
        <VoicemailBlue width="19.5" height="19.5" viewBox="0 -4 18 28" />
      ),
      to: "/voicemails",
      label: "Broadcast Data",
      tooltipLabel: "Broadcast Data",
    },
  ];

  if (manageUsers) {
    drawerList.push({
      icon: <UsersWhite width="23.5" height="18.5" viewBox="0 0 23.5 18.5" />,
      activeIcon: (
        <UsersBlue width="23.5" height="18.5" viewBox="0 0 23.5 18.5" />
      ),
      to: "/users",
      label: "User Permissions",
      tooltipLabel: "User Permissions",
    });
  }

  if (isAppRoleAdmin) {
    drawerList.push({
      icon: <SettingsOutlinedIcon />,
      activeIcon: <SettingsOutlinedIcon />,
      to: "/admin",
      label: "Admin",
      tooltipLabel: "Admin",
    });
  }

  /*
  if (overrideDefaultCallerId) {
    drawerList.push({
      icon: <ContactPhoneIcon />,
      activeIcon: <ContactPhoneIcon />,
      to: "/defaultCallerId",
      label: "Default Caller Id",
      tooltipLabel: "Default Caller Id",
    });
  }
  */

  useEffect(() => {
    if (appAccess) {
      fetchExtensions(true);
      fetchSites();
      fetchVoicemails();
      fetchUserRoles();
      fetchPhoneNumbers();
      fetchAccountInfo();
      fetchCountryList();
      fetchDefaultCallerId();
    }
  }, []);

  return (
    <DefaultLayout drawerList={drawerList}>
      <Routes>
        <Route path="/voicemails">
          <Route
            index
            element={
              <PrivateRoute
                element={
                  appAccess ? (
                    <Dashboard handleJwksResponse={handleJwksResponse} />
                  ) : (
                    <AccessDenied open={true} />
                  )
                }
              />
            }
          />
          <Route
            path=":id"
            element={
              <PrivateRoute
                element={
                  appAccess ? <Dashboard /> : <AccessDenied open={true} />
                }
              />
            }
          />
        </Route>
        <Route
          path="/users"
          element={
            <PrivateRoute
              element={
                manageUsers && appAccess ? <Users /> : <Navigate to="/" />
              }
            />
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute
              element={
                isAppRoleAdmin && appAccess ? <Admin /> : <Navigate to="/" />
              }
            />
          }
        />
        <Route
          path="/defaultCallerId"
          element={
            <PrivateRoute
              element={
                overrideDefaultCallerId && appAccess ? (
                  <DefaultCallerId />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
          }
        />
        <Route path="/token" element={<TokenComponent />} />
        <Route path="/" element={<Navigate to="/voicemails" />} />{" "}
        <Route path="/access-denied" element={<AccessDenied open={true} />} />
      </Routes>
      {state.warningDialog.map((item) => (
        <Warning
          open={!!item}
          warningMessage={item}
          onClose={() => {
            setState((prevState) => {
              const warningDialog = prevState.warningDialog.filter(
                (warning) => warning !== item
              );
              return {
                ...prevState,
                warningDialog,
              };
            });
          }}
        />
      ))}
    </DefaultLayout>
  );
};

export default App;
